import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './context/AuthContext';
import AnalyticsBridge from '@/components/AnalyticsBridge';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureProvider } from './context/FeatureContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { FilmProvider } from './context/FilmContext';
import { InvitationProvider } from './context/InvitationContext';
import Auth from './pages/Auth';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Home from './pages/Home';
import Landing from './pages/Landing';
import WorkspaceHome from './pages/WorkspaceHome';
import FilmDashboard from './pages/FilmDashboard';
import SceneBoard from './pages/SceneBoard';
import SceneDetail from './pages/SceneDetail';
import SceneList from './pages/SceneList';
import ShotList from './pages/ShotList';
import Profile from './pages/Profile';
import FilmManagement from './pages/FilmManagement';
import Invitations from './pages/Invitations';
import Billing from './pages/Billing';
import HelpCenter from './pages/HelpCenter';
import WorkspaceListPage from './pages/WorkspaceListPage';
import WorkspaceMembers from './pages/WorkspaceMembers';
import WorkspaceSettings from './pages/WorkspaceSettings';
import WorkspacePurchase from './pages/WorkspacePurchase';
import WorkspaceCreate from './pages/WorkspaceCreate';
import { AdminSupport } from './pages/admin';
import AppShell from './layouts/AppShell';
import CalendarPage from './pages/CalendarPage';
import GearPage from './pages/GearPage';
import FilesPage from './pages/FilesPage';
import Team from './pages/Team';
import AIInsights from './pages/AIInsights';
import AIBreakdown from './pages/AIBreakdown';
import CallSheetPage from './pages/CallSheetPage';
import CallSheetPublic from './pages/CallSheetPublic';
import CoveragePage from './pages/CoveragePage';
import DprPage from './pages/DprPage';
import { Toaster } from './components/ui/sonner';
import BetaBanner from './components/BetaBanner';
import GlobalUpgradeModal from './components/GlobalUpgradeModal';
import AdminRoute from './components/AdminRoute';
import RouteContextBridge from './components/RouteContextBridge';
import LegacyFilmRedirect from './components/LegacyFilmRedirect';
import './App.css';

function GtagRouteTracker() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: pathname + search,
    });
  }, [pathname, search]);

  return null;
}

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/" replace />;
};

// Admin routes opt out of AppShell because the admin module ships its own shell.
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" replace />;
};

// Phase 1 Task 5 — `/` is the public marketing landing page for logged-out
// visitors; authenticated users keep the existing AppShell + Home behavior
// at the exact same URL (single-workspace auto-redirect etc. unchanged).
const RootRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) return <Landing />;

  return (
    <AppShell>
      <Home />
    </AppShell>
  );
};

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <WorkspaceProvider>
              <FilmProvider>
                <InvitationProvider>
                  <FeatureProvider>
                    <BrowserRouter>
                      <GtagRouteTracker />
                      <AnalyticsBridge />
                      <RouteContextBridge />
                    <Routes>
                      {/* Public / unauthenticated */}
                      <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />

                      {/* Public legal documents — must be reachable without
                          authentication (compliance, deep-links from emails,
                          GDPR access). Own minimal chrome via LegalShell. */}
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms"   element={<TermsOfService />} />

                      {/* Crew call-sheet link (M3.2) — no account needed; the
                          token in the URL is the credential. */}
                      <Route path="/cs/:token" element={<CallSheetPublic />} />

                      {/* Root — public landing for logged-out visitors,
                          AppShell + Home for authenticated users (Task 5). */}
                      <Route path="/" element={<RootRoute />} />

                      {/* Authenticated app — wrapped in AppShell */}
                      <Route element={<AppShell />}>
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/billing" element={<Billing />} />
                        <Route path="/invitations" element={<Invitations />} />

                        {/* Calendar routes — hybrid scope */}
                        <Route path="/calendar" element={<CalendarPage />} />
                        <Route path="/assets" element={<Navigate to="/gear" replace />} />
                        <Route path="/gear" element={<GearPage />} />
                        <Route path="/files" element={<FilesPage />} />
                        <Route path="/team" element={<Team />} />
                        <Route path="/ai-insights" element={<AIInsights />} />

                        {/* Workspace Purchase Flow */}
                        <Route path="/workspace/purchase" element={<WorkspacePurchase />} />
                        <Route path="/workspace/create" element={<WorkspaceCreate />} />

                        {/* Workspace-scoped routes */}
                        <Route path="/workspaces" element={<WorkspaceListPage />} />
                        <Route path="/workspaces/:workspaceId" element={<WorkspaceHome />} />
                        <Route path="/workspaces/:workspaceId/members" element={<WorkspaceMembers />} />
                        <Route path="/workspaces/:workspaceId/settings" element={<WorkspaceSettings />} />
                        <Route path="/workspaces/:workspaceId/calendar" element={<CalendarPage />} />
                        <Route path="/workspaces/:workspaceId/gear" element={<GearPage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId" element={<FilmDashboard />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/manage" element={<FilmManagement />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/scenes" element={<SceneList />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/shots" element={<ShotList />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/board" element={<SceneBoard />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/calendar" element={<CalendarPage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/gear" element={<GearPage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/files" element={<FilesPage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/ai-breakdown" element={<AIBreakdown />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/days/:scheduleId/call-sheet" element={<CallSheetPage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/days/:scheduleId/coverage" element={<CoveragePage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/set" element={<CoveragePage />} />
                        <Route path="/workspaces/:workspaceId/films/:filmId/days/:scheduleId/dpr" element={<DprPage />} />

                        {/* Legacy film routes — Phase 1 Task 4.2: the
                            workspace-prefixed tree above is canonical; old
                            /films/:id bookmarks resolve the film's workspace
                            and redirect (subpath + query preserved). */}
                        <Route path="/films/:filmId" element={<LegacyFilmRedirect />} />
                        <Route path="/films/:filmId/*" element={<LegacyFilmRedirect />} />
                        <Route path="/scenes/:sceneId" element={<SceneDetail />} />
                        <Route path="/help" element={<HelpCenter />} />

                        {/* Catch-all — orphan paths fall back to home so we
                            never render a blank screen on a dead route. */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Route>

                      {/* Admin Support Panel — opts out of AppShell (has its own shell).
                         AdminRoute silently redirects non-admins to `/`. */}
                      <Route
                        path="/admin/support/*"
                        element={
                          <ProtectedRoute>
                            <AdminRoute>
                              <AdminSupport />
                            </AdminRoute>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </BrowserRouter>
                  <GlobalUpgradeModal />
                  <Toaster />
                </FeatureProvider>
              </InvitationProvider>
            </FilmProvider>
          </WorkspaceProvider>
        </AuthProvider>
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </div>
  );
}

export default App;
